import React, { useState, useEffect, useRef, useCallback } from "react"
import {
  Box,
  Button,
  Typography,
  Slider,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material"

const API_BASE = process.env.REACT_APP_API_BASE_URL || ""
const IMAGE_SIZE = 1024

const HoldMapping = () => {
  const [placements, setPlacements] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [maskOpacity, setMaskOpacity] = useState(0.7)
  const [showMatched, setShowMatched] = useState(true)
  const [showUnmatched, setShowUnmatched] = useState(true)
  const [hoveredPlacement, setHoveredPlacement] = useState(null)
  const [dragPlacementId, setDragPlacementId] = useState(null)
  const [dragPos, setDragPos] = useState(null)
  const [debugInfo, setDebugInfo] = useState("")
  const [selectedImage, setSelectedImage] = useState("")
  const [trainImageNames, setTrainImageNames] = useState([])
  const [previewData, setPreviewData] = useState(null)
  const colorMaskCanvasRef = useRef(null)
  const svgRef = useRef(null)
  const maskLoadedRef = useRef(false)
  const dragRef = useRef({ placementId: null, pos: null })

  // Fetch mapping data + train image names
  useEffect(() => {
    setLoading(true)
    fetch(`${API_BASE}/api/admin/hold-color-mapping`)
      .then((r) => r.json())
      .then((data) => {
        setPlacements(data.placements)
        setTrainImageNames(data.train_image_names || [])
        setLoading(false)
      })
      .catch((err) => {
        setError(err.message)
        setLoading(false)
      })
  }, [])

  // Load color mask into hidden canvas for pixel sampling
  useEffect(() => {
    const canvas = colorMaskCanvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext("2d")
    const img = new Image()
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)
      maskLoadedRef.current = true
      setDebugInfo(`Mask loaded: ${img.width}x${img.height}`)
    }
    img.onerror = (err) => {
      setDebugInfo(`Mask failed to load: ${err}`)
    }
    img.src = "/images/kilter-color-holds.png"
  }, [])

  // Fetch preview when training image is selected
  useEffect(() => {
    if (!selectedImage) {
      setPreviewData(null)
      return
    }
    fetch(
      `${API_BASE}/api/admin/hold-color-mapping/preview/${encodeURIComponent(
        selectedImage
      )}`
    )
      .then((r) => r.json())
      .then(setPreviewData)
      .catch(console.error)
  }, [selectedImage])

  const sampleColor = useCallback((x, y) => {
    const canvas = colorMaskCanvasRef.current
    if (!canvas || !maskLoadedRef.current) return [0, 0, 0]
    const ctx = canvas.getContext("2d")
    const px = Math.max(0, Math.min(Math.round(x), canvas.width - 1))
    const py = Math.max(0, Math.min(Math.round(y), canvas.height - 1))
    const pixel = ctx.getImageData(px, py, 1, 1).data
    return [pixel[0], pixel[1], pixel[2]]
  }, [])

  const getSvgPoint = useCallback((e) => {
    const svg = svgRef.current
    if (!svg) return null
    const pt = svg.createSVGPoint()
    pt.x = e.clientX
    pt.y = e.clientY
    const ctm = svg.getScreenCTM()
    if (!ctm) return null
    return pt.matrixTransform(ctm.inverse())
  }, [])

  const handleCircleMouseDown = useCallback((e, p) => {
    if (p.matched) return
    e.preventDefault()
    e.stopPropagation()
    const pos = { x: p.square_x, y: p.square_y }
    dragRef.current = { placementId: p.placement_id, pos }
    setDragPlacementId(p.placement_id)
    setDragPos(pos)
    setDebugInfo(`Drag started: placement ${p.placement_id}`)
  }, [])

  const handleSvgMouseMove = useCallback(
    (e) => {
      if (dragRef.current.placementId === null) return
      const pt = getSvgPoint(e)
      if (pt) {
        dragRef.current.pos = { x: pt.x, y: pt.y }
        setDragPos({ x: pt.x, y: pt.y })
      }
    },
    [getSvgPoint]
  )

  const handleSvgMouseUp = useCallback(() => {
    const { placementId, pos } = dragRef.current
    if (placementId === null || !pos) {
      setDebugInfo((prev) => prev + " | mouseUp with no drag active")
      dragRef.current = { placementId: null, pos: null }
      setDragPlacementId(null)
      setDragPos(null)
      return
    }
    const x = Math.round(pos.x)
    const y = Math.round(pos.y)
    const inBounds = x >= 0 && x < IMAGE_SIZE && y >= 0 && y < IMAGE_SIZE
    const color = inBounds ? sampleColor(x, y) : [0, 0, 0]
    const colorSum = color[0] + color[1] + color[2]
    setDebugInfo(
      `Drop: placement ${placementId} at (${x}, ${y}) | inBounds=${inBounds} | color=[${color}] sum=${colorSum} | maskLoaded=${maskLoadedRef.current}`
    )
    if (inBounds && colorSum > 30) {
      setPlacements((prev) =>
        prev.map((p) =>
          p.placement_id === placementId
            ? { ...p, color, matched: true, square_x: x, square_y: y }
            : p
        )
      )
    }
    dragRef.current = { placementId: null, pos: null }
    setDragPlacementId(null)
    setDragPos(null)
  }, [sampleColor])

  const handleSave = async () => {
    setSaving(true)
    try {
      const res = await fetch(`${API_BASE}/api/admin/hold-color-mapping`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(placements),
      })
      if (!res.ok) throw new Error("Save failed")
      setError(null)
    } catch (err) {
      setError(err.message)
    }
    setSaving(false)
  }

  const handleRecompute = async () => {
    setLoading(true)
    try {
      const res = await fetch(
        `${API_BASE}/api/admin/hold-color-mapping?recompute=1`
      )
      const data = await res.json()
      setPlacements(data.placements)
      setError(null)
    } catch (err) {
      setError(err.message)
    }
    setLoading(false)
  }

  const inBoundsPlacements = placements.filter((p) => p.in_bounds)
  const matchedCount = inBoundsPlacements.filter((p) => p.matched).length
  const unmatchedCount = inBoundsPlacements.filter((p) => !p.matched).length

  const visiblePlacements = inBoundsPlacements.filter((p) => {
    if (p.matched && !showMatched) return false
    if (!p.matched && !showUnmatched) return false
    return true
  })

  // Render matched first so unmatched (red) circles are on top
  const sortedPlacements = [
    ...visiblePlacements.filter((p) => p.matched),
    ...visiblePlacements.filter((p) => !p.matched),
  ]

  return (
    <Box p={2}>
      <canvas ref={colorMaskCanvasRef} style={{ display: "none" }} />
      {loading && (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Controls */}
      <Box display="flex" gap={2} mb={2} alignItems="center" flexWrap="wrap">
        <Typography variant="body2">
          Matched: {matchedCount} | Unmatched: {unmatchedCount} | Total:{" "}
          {placements.length}
        </Typography>
        <Button
          variant="contained"
          size="small"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button variant="outlined" size="small" onClick={handleRecompute}>
          Recompute
        </Button>
      </Box>

      <Box display="flex" gap={3} mb={2} alignItems="center" flexWrap="wrap">
        <Box sx={{ minWidth: 150 }}>
          <Typography variant="caption">Mask Opacity</Typography>
          <Slider
            value={maskOpacity}
            onChange={(e, v) => setMaskOpacity(v)}
            min={0}
            max={1}
            step={0.01}
            size="small"
          />
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={showMatched}
              onChange={(e) => setShowMatched(e.target.checked)}
              size="small"
            />
          }
          label="Matched"
        />
        <FormControlLabel
          control={
            <Switch
              checked={showUnmatched}
              onChange={(e) => setShowUnmatched(e.target.checked)}
              size="small"
            />
          }
          label="Unmatched"
        />
      </Box>

      {/* Training image dropdown */}
      <Box mb={2}>
        <FormControl size="small" sx={{ minWidth: 300 }}>
          <InputLabel>Training Image (preview)</InputLabel>
          <Select
            value={selectedImage}
            onChange={(e) => setSelectedImage(e.target.value)}
            label="Training Image (preview)"
          >
            <MenuItem value="">
              <em>Square space (default)</em>
            </MenuItem>
            {trainImageNames.map((name) => (
              <MenuItem key={name} value={name}>
                {name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Hover info panel */}
      <Box
        sx={{
          mb: 1,
          p: 1,
          bgcolor: "#f5f5f5",
          color: "#000",
          borderRadius: 1,
          fontFamily: "monospace",
          fontSize: "0.75rem",
          minHeight: 24,
        }}
      >
        {hoveredPlacement ? (
          <>
            Placement: {hoveredPlacement.placement_id} | Hole:{" "}
            {hoveredPlacement.hole_id} | Grid: ({hoveredPlacement.hole_x},{" "}
            {hoveredPlacement.hole_y}) | Pixel: ({hoveredPlacement.square_x},{" "}
            {hoveredPlacement.square_y}) | Color: [
            {hoveredPlacement.color.join(", ")}] |{" "}
            {hoveredPlacement.matched ? "Matched" : "Unmatched"}
            {hoveredPlacement.matched && (
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  marginLeft: 8,
                  backgroundColor: `rgb(${hoveredPlacement.color.join(",")})`,
                  border: "1px solid #333",
                  verticalAlign: "middle",
                }}
              />
            )}
          </>
        ) : (
          <span style={{ opacity: 0.5 }}>
            Hover over a placement to see details. Drag red circles onto colored
            areas to assign.
          </span>
        )}
      </Box>

      {/* Debug info */}
      {debugInfo && (
        <Box
          sx={{
            mb: 1,
            p: 1,
            bgcolor: "#fff3cd",
            color: "#664d03",
            borderRadius: 1,
            fontFamily: "monospace",
            fontSize: "0.75rem",
          }}
        >
          Debug: {debugInfo}
        </Box>
      )}

      {/* Main image view */}
      <Box
        sx={{
          position: "relative",
          width: "100%",
          maxWidth: IMAGE_SIZE,
          aspectRatio: "1/1",
          overflow: "hidden",
          border: "1px solid #ccc",
          userSelect: "none",
        }}
      >
        <img
          src="/images/kilter.jpg"
          alt="kilter board"
          style={{ width: "100%", height: "100%", display: "block" }}
          draggable={false}
        />
        <img
          src="/images/kilter-color-holds.png"
          alt="color mask"
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            opacity: maskOpacity,
            pointerEvents: "none",
          }}
          draggable={false}
        />
        <svg
          ref={svgRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
          }}
          viewBox={`0 0 ${IMAGE_SIZE} ${IMAGE_SIZE}`}
          onMouseMove={handleSvgMouseMove}
          onMouseUp={handleSvgMouseUp}
          onMouseLeave={handleSvgMouseUp}
        >
          {sortedPlacements.map((p) => {
            const isDragging = dragPlacementId === p.placement_id
            const cx = isDragging && dragPos ? dragPos.x : p.square_x
            const cy = isDragging && dragPos ? dragPos.y : p.square_y
            const isHovered =
              hoveredPlacement?.placement_id === p.placement_id

            return (
              <circle
                key={p.placement_id}
                cx={cx}
                cy={cy}
                r={isHovered || isDragging ? 8 : 5}
                fill={
                  p.matched
                    ? `rgb(${p.color[0]},${p.color[1]},${p.color[2]})`
                    : "red"
                }
                stroke={isDragging ? "cyan" : p.matched ? "white" : "yellow"}
                strokeWidth={isDragging ? 2 : 1}
                style={{ cursor: p.matched ? "default" : "grab" }}
                onMouseDown={(e) => handleCircleMouseDown(e, p)}
                onMouseEnter={() => setHoveredPlacement(p)}
                onMouseLeave={() => {
                  if (!isDragging) setHoveredPlacement(null)
                }}
              />
            )
          })}
        </svg>
      </Box>

      {/* Training image preview */}
      {previewData && (
        <Box mt={3}>
          <Typography variant="subtitle1" gutterBottom>
            Preview: {selectedImage}
          </Typography>
          <Box
            sx={{
              position: "relative",
              display: "inline-block",
              border: "1px solid #ccc",
            }}
          >
            <img
              src={`data:image/jpg;base64,${previewData.train_image}`}
              alt="training"
              style={{ maxWidth: "100%", display: "block" }}
            />
            <img
              src={`data:image/png;base64,${previewData.warped_mask}`}
              alt="warped mask"
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                opacity: maskOpacity,
                pointerEvents: "none",
              }}
            />
          </Box>
        </Box>
      )}
    </Box>
  )
}

export default HoldMapping
